import { Card, Flex, Layout, Spin, Typography } from "antd"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router"

import NewPasswordForm from "../components/Forms/NewPasswordForm"
import LoginHeader from "../components/LoginHeader.tsx"
import PrivacyPolicy from "../components/PrivacyPolicy"
import { generateDocumentTitle } from "../constants/document-title"
import type { GetActionArg } from "../redux/shared/types.ts"
import type { AppDispatch, RootState } from "../store.ts"
import { authErrorReset } from "./LoginPage/redux/auth.slice"
import { createUserPassword as createUserPasswordThunk } from "./LoginPage/redux/auth.thunks.js"

const { Title, Paragraph } = Typography

function NewPasswordPage({
  error,
  fetching,
  createUserPassword,
  resetError
}: ConnectedProps<typeof connector>) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  function handleSubmit(password: string) {
    createUserPassword({ data: { password, isNewUserCheck: false }, navigate })
  }

  useEffect(() => {
    generateDocumentTitle("Create New Password")
  }, [])

  useEffect(() => {
    if (error) resetError()
  }, [error, resetError])

  return (
    <Layout style={{ backgroundColor: "#e6f2f5", minHeight: "100vh" }}>
      <Spin size="large" spinning={fetching}>
        <Flex
          vertical
          align="center"
          justify="center"
          style={{ minHeight: "100vh" }}
        >
          <LoginHeader />

          <Card
            className="login-card"
            style={{ margin: "auto", width: "100%", maxWidth: "540px" }}
          >
            <Flex vertical align="center" style={{ marginBottom: 16 }}>
              <Title level={2} style={{ marginBottom: 6 }}>
                {t("Create New Password")}
              </Title>
              <Paragraph type="secondary" style={{ fontSize: 16, margin: 0 }}>
                {t(
                  "Your new password must be different from previously used passwords"
                )}
              </Paragraph>
            </Flex>

            <NewPasswordForm onSubmit={handleSubmit} error={error} />
          </Card>

          <PrivacyPolicy />
        </Flex>
      </Spin>
    </Layout>
  )
}

const mapStateToProps = (state: RootState) => ({
  error: state.auth.error,
  fetching: state.auth.fetching,
  isMobile: state.nav.isMobile
})

// wraps dispatch to create nicer functions to call within our component
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  resetError: () => dispatch(authErrorReset(undefined)),
  createUserPassword: (data: GetActionArg<typeof createUserPasswordThunk>) =>
    dispatch(createUserPasswordThunk(data))
})

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(NewPasswordPage)
